import { Link } from '@inertiajs/react'
import {
  DollarSign,
  TrendingUp,
  ArrowLeftRight,
  FileText,
  CreditCard,
  Plus,
  ArrowRight,
  ExternalLink,
} from 'lucide-react'
import AppLayout from '@/layouts/AppLayout'
import BalanceCard from '@/components/BalanceCard'
import StatusBadge from '@/components/StatusBadge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { DashboardProps } from '@/types'

export default function Dashboard({
  balance,
  outstanding_estimates,
  total_payments,
  total_finalized,
  net_adjustments,
  recent_pos,
  recent_payments,
}: DashboardProps) {
  return (
    <AppLayout>
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Overview of your balance ledger
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm">
            <Link href="/payments">
              <CreditCard className="size-4" />
              Record Payment
            </Link>
          </Button>
          <Button asChild size="sm">
            <Link href="/purchase_orders/new">
              <Plus className="size-4" />
              New PO
            </Link>
          </Button>
        </div>
      </div>

      {/* Balance + Outstanding */}
      <div className="grid gap-4 md:grid-cols-2 mb-4">
        <BalanceCard balance={balance} />

        <Card className="border-0 bg-card relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-warning/50 to-transparent" />
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-muted-foreground">Outstanding Estimates</p>
            <p className="mt-2 text-4xl font-bold tracking-tight text-warning">
              {formatCurrency(parseFloat(outstanding_estimates))}
            </p>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Pending finalization
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Stats row */}
      <div className="grid gap-4 md:grid-cols-3 mb-8">
        <Card className="border-border/50">
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
              <DollarSign className="size-5 text-primary" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Total Payments</p>
              <p className="text-xl font-semibold text-foreground">
                {formatCurrency(parseFloat(total_payments))}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="flex size-10 items-center justify-center rounded-lg bg-positive/10">
              <TrendingUp className="size-5 text-positive" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Total Finalized</p>
              <p className="text-xl font-semibold text-foreground">
                {formatCurrency(parseFloat(total_finalized))}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
              <ArrowLeftRight className="size-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Net Adjustments</p>
              <p className="text-xl font-semibold text-foreground">
                {formatCurrency(parseFloat(net_adjustments))}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tables */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Purchase Orders */}
        <Card className="border-border/50">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-base">Recent Purchase Orders</CardTitle>
            <Button asChild variant="ghost" size="sm" className="text-xs text-muted-foreground">
              <Link href="/purchase_orders">
                View all <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recent_pos.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No purchase orders yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead className="text-xs text-muted-foreground">ID</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Amount</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Status</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent_pos.map((po) => (
                    <TableRow key={po.id} className="border-border/30">
                      <TableCell>
                        <Link
                          href={`/purchase_orders/${po.id}`}
                          className="text-primary hover:underline font-medium"
                        >
                          #{po.id}
                        </Link>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatCurrency(parseFloat(po.amount))}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={po.status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(po.date)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Recent Payments */}
        <Card className="border-border/50">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-base">Recent Payments</CardTitle>
            <Button asChild variant="ghost" size="sm" className="text-xs text-muted-foreground">
              <Link href="/payments">
                View all <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recent_payments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No payments yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50 hover:bg-transparent">
                    <TableHead className="text-xs text-muted-foreground">Amount</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Date</TableHead>
                    <TableHead className="text-xs text-muted-foreground">PO</TableHead>
                    <TableHead className="text-xs text-muted-foreground">Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent_payments.map((payment) => (
                    <TableRow key={payment.id} className="border-border/30">
                      <TableCell className="font-mono text-sm">
                        {formatCurrency(parseFloat(payment.amount))}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(payment.date)}
                      </TableCell>
                      <TableCell>
                        {payment.purchase_order_id ? (
                          <Link
                            href={`/purchase_orders/${payment.purchase_order_id}`}
                            className="text-primary hover:underline text-sm"
                          >
                            #{payment.purchase_order_id}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground text-sm">--</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm max-w-[120px] truncate">
                        {payment.notes || '--'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
